import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { BarChart3, Loader2, AlertTriangle, Download, MousePointerClick, CheckCircle2, TrendingUp, Info } from 'lucide-react';
import { fmtDateTime } from '../lib/dateFormat';

const VERTICALS = ['', 'flight', 'hotel', 'experience', 'visa', 'store'];

function BreakdownTable({ title, rows }) {
  return (
    <Card className="p-4 rounded-2xl">
      <h3 className="font-semibold text-sm mb-2">{title}</h3>
      {(!rows || rows.length === 0) ? <p className="text-xs text-muted-foreground">No data.</p> : (
        <table className="w-full text-sm"><tbody>
          {rows.slice(0, 8).map((r) => (
            <tr key={r.key} className="border-b border-border last:border-0">
              <td className="py-1.5 truncate max-w-[160px]">{r.key}</td>
              <td className="py-1.5 text-right font-semibold tabular">{r.clicks}</td>
              <td className="py-1.5 text-right text-xs text-muted-foreground tabular">₹{Math.round(r.estimated).toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody></table>
      )}
    </Card>
  );
}

export default function AdminAffiliatePerformance() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ date_from: '', date_to: '', vertical: '', include_demo: false });

  const load = useCallback(() => {
    const params = { ...f };
    api.affiliateAnalytics(params).then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
    api.affiliateClicksList({ ...params, limit: 50 }).then(({ data }) => setClicks(data.clicks)).catch(() => {});
  }, [f]);
  useEffect(() => { document.title = 'Affiliate performance | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const exportCsv = async () => {
    try {
      const { data: blob } = await api.affiliateExportCsv(f);
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'affiliate_clicks.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };
  const openDetail = (id) => api.affiliateClickDetail(id).then(({ data }) => setDetail(data)).catch(() => {});

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Admin access required</h1></div></Layout>;

  const s = data?.summary;
  const KPI = ({ icon: Ic, label, value, sub }) => (
    <Card className="p-4 rounded-2xl"><div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Ic className="h-4 w-4 text-accent" /> {label}</div><p className="text-2xl font-semibold tabular">{value}</p>{sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}</Card>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2 mb-1"><BarChart3 className="h-6 w-6 text-accent" /> Central affiliate performance</h1>
        <p className="text-sm text-muted-foreground mb-5">One unified view across all verticals. Demo events are excluded unless toggled on.</p>

        <Card className="p-4 rounded-2xl mb-5" data-testid="affiliate-filters">
          <div className="grid gap-3 md:grid-cols-5 items-end">
            <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1" data-testid="filter-from" /></div>
            <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1" data-testid="filter-to" /></div>
            <div><label className="text-xs text-muted-foreground">Vertical</label>
              <Select value={f.vertical || 'all'} onValueChange={(v) => setF({ ...f, vertical: v === 'all' ? '' : v })}>
                <SelectTrigger className="mt-1" data-testid="filter-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{VERTICALS.map((v) => <SelectItem key={v || 'all'} value={v || 'all'}>{v ? v[0].toUpperCase() + v.slice(1) : 'All verticals'}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1"><Switch checked={f.include_demo} onCheckedChange={(v) => setF({ ...f, include_demo: v })} data-testid="filter-demo" /><span className="text-sm">Include demo</span></div>
            <Button onClick={exportCsv} variant="outline" className="rounded-xl" data-testid="export-csv"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
          </div>
        </Card>

        {!s ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <>
            <div className="grid gap-3 grid-cols-2 md:grid-cols-6 mb-5" data-testid="affiliate-kpis">
              <KPI icon={MousePointerClick} label="Total clicks" value={s.total_clicks} />
              <KPI icon={CheckCircle2} label="Confirmed conv." value={s.confirmed_conversions} sub={`${s.pending_conversions} pending`} />
              <KPI icon={TrendingUp} label="Est. commission" value={`₹${Math.round(s.estimated_commission).toLocaleString('en-IN')}`} sub="projection" />
              <KPI icon={TrendingUp} label="Confirmed comm." value={`₹${Math.round(s.confirmed_commission).toLocaleString('en-IN')}`} sub="postbacks only" />
              <KPI icon={BarChart3} label="EPC" value={`₹${s.epc}`} />
              <KPI icon={BarChart3} label="CTR" value={`${s.ctr}%`} sub={`${s.impressions} impr.`} />
            </div>
            <div className="rounded-xl border border-border bg-secondary/50 p-3 text-xs text-muted-foreground flex items-start gap-2 mb-5"><Info className="h-4 w-4 mt-0.5 shrink-0 text-accent" /> {data.note}</div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
              <BreakdownTable title="By vertical" rows={data.by_vertical} />
              <BreakdownTable title="By partner" rows={data.by_partner} />
              <BreakdownTable title="By placement" rows={data.by_placement} />
              <BreakdownTable title="By campaign" rows={data.by_campaign} />
              <BreakdownTable title="By creator" rows={data.by_creator} />
              <BreakdownTable title="By destination" rows={data.by_destination} />
            </div>

            <Card className="p-4 rounded-2xl">
              <h3 className="font-semibold mb-3">Recent clicks</h3>
              <div className="overflow-auto max-h-96">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">When</th><th>Vertical</th><th>Partner</th><th>Placement</th><th>Demo</th><th></th></tr></thead>
                  <tbody data-testid="clicks-table">{clicks.map((c) => (
                    <tr key={c.click_id} className="border-b border-border last:border-0">
                      <td className="py-1.5 text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</td>
                      <td><Badge variant="outline" className="text-[10px]">{c.vertical || c.offer_type}</Badge></td>
                      <td className="text-xs">{c.partner_id || '—'}</td>
                      <td className="text-xs">{c.placement || '—'}</td>
                      <td>{c.is_demo ? <Badge variant="outline" className="text-[10px] text-amber-600">demo</Badge> : ''}</td>
                      <td className="text-right"><button onClick={() => openDetail(c.click_id)} className="text-accent text-xs font-medium" data-testid={`click-detail-${c.click_id}`}>Detail</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </Card>

            {detail && (
              <Card className="p-4 rounded-2xl mt-4" data-testid="click-detail-panel">
                <div className="flex items-center justify-between mb-2"><h3 className="font-semibold">Click {detail.click.click_id}</h3><button onClick={() => setDetail(null)} className="text-xs text-muted-foreground">Close</button></div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  {Object.entries(detail.click).filter(([k]) => !['target_url'].includes(k)).map(([k, v]) => (
                    <div key={k} className="flex justify-between rounded-lg border border-border px-2.5 py-1.5"><span className="text-muted-foreground text-xs">{k}</span><span className="font-medium text-xs truncate max-w-[140px]">{String(v)}</span></div>
                  ))}
                </div>
                <p className="mt-2 text-xs">Conversion status: <Badge variant="outline">{detail.conversion_status}</Badge> · Commission: <Badge variant="outline">{detail.commission_status}</Badge></p>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
